import { forwardRef, useId, useRef, useState } from 'react'
import { Paperclip, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn, formatFileSize } from '@shared/lib'
import { ErrorMessage } from '@shared/ui/ErrorMessage/ErrorMessage'
import { Label } from '@shared/ui/Label/Label'
import { FileInputVariant } from './types'

export interface FileInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'value'
> {
  label?: string
  error?: string
  currentFile?: string
  isLoading?: boolean
  wrapperClassName?: string
  showImagePreview?: boolean
  variant?: FileInputVariant
  /** Empty-state text; defaults to the shared «click to attach» copy. */
  placeholder?: string
  /** Controlled display: the picked file from form state. Without it the
   *  selection lives in internal state and dies with the component — a
   *  wizard step that unmounts on navigation would come back looking empty
   *  while the form still holds the file. Pass `null` for «nothing picked». */
  value?: File | null
  /** Renders a trash button next to a freshly picked file; the callback must
   *  clear the form value. Clearing reverts to `currentFile` (edit) or the
   *  empty state. A saved `currentFile` alone shows no trash — replacing is
   *  the only action on a stored file. */
  onRemove?: () => void
  removeLabel?: string
}

// A stored template arrives as a URL; only its basename is human-readable.
function fileBaseName(path: string): string {
  return path.split('/').pop()?.split('?')[0] || path
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      className,
      label,
      error,
      id,
      currentFile,
      isLoading,
      wrapperClassName,
      onChange,
      showImagePreview,
      variant = FileInputVariant.inline,
      placeholder,
      value,
      onRemove,
      removeLabel,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslation('common')
    const generatedId = useId()
    const inputId = id ?? (label ? generatedId : undefined)
    const innerRef = useRef<HTMLInputElement | null>(null)
    const [selectedFile, setSelectedFile] = useState<{ name: string; size: number } | null>(null)
    const [previewDataUrl, setPreviewDataUrl] = useState<string | null>(null)

    // Controlled (`value` prop present) wins over the internal selection.
    const pickedFile = value !== undefined ? value : selectedFile

    const isBoxed = variant === FileInputVariant.boxed
    const hasFile = Boolean(pickedFile || currentFile)
    const isDisabled = isLoading || props.disabled
    const imagePreview = previewDataUrl ?? (showImagePreview && currentFile ? currentFile : null)
    const emptyText = placeholder ?? t('file_input.click_to_attach')

    // inline keeps the legacy `name (size)` / «change file» copy; boxed shows
    // the bare file name per the wizard mock.
    const inlineFileText = pickedFile
      ? `${pickedFile.name} (${formatFileSize(pickedFile.size)})`
      : (currentFile ?? null)
    const displayText = isBoxed
      ? (pickedFile?.name ?? (currentFile ? fileBaseName(currentFile) : emptyText))
      : inlineFileText && showImagePreview
        ? t('file_input.change_file')
        : (inlineFileText ?? emptyText)

    // The forwarded ref is usually RHF's callback ref; a merged inner ref lets
    // handleRemove reset the native input so re-picking the same file fires
    // a change event again.
    const setRefs = (node: HTMLInputElement | null) => {
      innerRef.current = node
      if (typeof ref === 'function') ref(node)
      else if (ref) ref.current = node
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      const file = e.target.files?.[0]
      if (file) {
        setSelectedFile({ name: file.name, size: file.size })
        if (showImagePreview && file.type.startsWith('image/')) {
          const reader = new FileReader()
          reader.onload = (ev) => setPreviewDataUrl((ev.target?.result as string) ?? null)
          reader.readAsDataURL(file)
        } else {
          setPreviewDataUrl(null)
        }
      } else {
        setSelectedFile(null)
        setPreviewDataUrl(null)
      }
      onChange?.(e)
    }

    function handleRemove() {
      if (innerRef.current) innerRef.current.value = ''
      setSelectedFile(null)
      setPreviewDataUrl(null)
      onRemove?.()
    }

    return (
      // `relative` держит скрытый input внутри обёртки: `sr-only` — это
      // `position: absolute`, и без позиционированного предка он привязывается
      // к начальному содержащему блоку. У поля ниже сгиба это раздувало
      // прокрутку самого `<html>`, и уезжала вся оболочка вместе с шапкой и
      // сайдбаром (поймано на регистрации участника 2026-08-09).
      <div className={cn('relative flex flex-col gap-2', wrapperClassName)}>
        {label && (
          <Label htmlFor={inputId} required={props.required}>
            {label}
          </Label>
        )}
        <input
          ref={setRefs}
          id={inputId}
          type="file"
          className="sr-only"
          onChange={handleChange}
          disabled={isDisabled}
          {...props}
        />
        <div className="flex items-center gap-4">
          <label
            htmlFor={inputId}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1',
              (isBoxed || !hasFile) && 'border-border rounded-lg border px-3 py-2',
              isDisabled && 'cursor-not-allowed opacity-50',
              error && (isBoxed || !hasFile) && 'border-red',
              className
            )}
          >
            <Paperclip className="text-orange size-5 shrink-0" />
            <span className="text-orange typo-base">{displayText}</span>
            {isLoading && (
              <div className="border-t-orange ml-1 size-4 animate-spin rounded-full border-2 border-gray-200" />
            )}
          </label>
          {onRemove && pickedFile && (
            <button
              type="button"
              onClick={handleRemove}
              aria-label={removeLabel}
              className="text-orange shrink-0 cursor-pointer"
            >
              <Trash2 className="size-6" />
            </button>
          )}
        </div>
        {showImagePreview && imagePreview && (
          <img src={imagePreview} alt="preview" className="h-32 rounded-xl object-contain" />
        )}
        <ErrorMessage message={error} />
      </div>
    )
  }
)
FileInput.displayName = 'FileInput'
